import { defaultLangLocale, LangLocale } from '@myre/common-i18n';
import { getLanguagesForCurrentEnv } from '@myre/common-language';
import { compareByLowerCaseFactory } from '@myre/common-primitives';
import { TranslatePipe } from '@myre/ui-primitive-core';
import { DropdownComponent, type ISelectItem } from '@myre/ui-primitive-forms';
import { LayoutColumnComponent } from '@myre/ui-primitive-layouts';

import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  type OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { getYear } from 'date-fns';
import { CookieService } from 'ngx-cookie-service';

import {
  COOKIE_PREFIX,
  cookieOptions,
} from '../../../cookies/cookies.constants';

import { FooterImageComponent } from './footer-items/footer-image/footer-image.component';
import { FooterTextComponent } from './footer-items/footer-text/footer-text.component';
import { FooterUrlComponent } from './footer-items/footer-url/footer-url.component';
import { footerLinks } from './footer-links';

export interface IFooterLinks {
  header: string;
  items: IFooterItem[];
}

export interface IFooterItem {
  name: string;
  url: string | string[];
  isActive: boolean;
  authRequired?: boolean;
  target?: '_self' | '_blank' | '_parent' | '_top';
}

@Component({
  selector: 'myre-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropdownComponent,
    FooterImageComponent,
    FooterTextComponent,
    FooterUrlComponent,
    FormsModule,
    LayoutColumnComponent,
    TranslatePipe,
  ],
})
export class FooterComponent implements OnInit {
  @Input()
  public logoPath: string;

  @Input()
  public cookieSuffix: string;

  @Input()
  public domain: string;

  @Input()
  public set isAuth(isAuth: boolean) {
    this._isAuth = isAuth;

    this._filterLinks();
  }

  public get isAuth(): boolean {
    return this._isAuth;
  }

  private readonly _cookieService = inject(CookieService);
  private readonly _translateService = inject(TranslateService);

  private _isAuth: boolean;

  protected _links: IFooterLinks[];
  protected _selectedLang: LangLocale;
  protected _languageList: ISelectItem<LangLocale>[] =
    getLanguagesForCurrentEnv()
      .map(l => ({
        value: l.langLocale,
        label: l.nativeName,
      }))
      .sort(compareByLowerCaseFactory('label'));

  protected _copyright: string = `© ${getYear(
    new Date(),
  )} MYRE. ${this._translateService.instant('_ui.footer.copyright')}`;

  public ngOnInit(): void {
    this._selectedLang = this._getSelectedLang();
    this._filterLinks();
  }

  protected _getStringUrl(url: string | string[]): string | null {
    return typeof url === 'string' ? url : null;
  }

  protected _changeLanguage(lang: LangLocale): void {
    this._setCookie(lang);
    location.reload();
  }

  private _filterLinks(): void {
    this._links = footerLinks.map(list => ({
      header: list.header,
      items: list.items.filter(
        link => link.isActive && (!link.authRequired || this._isAuth),
      ),
    }));
  }

  private _getSelectedLang(): LangLocale {
    const selectedLang = this._cookieService.get(
      COOKIE_PREFIX + this.cookieSuffix,
    ) as LangLocale;

    return selectedLang || defaultLangLocale;
  }

  private _setCookie(lang: string): void {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);

    const options = { ...cookieOptions };
    options.expires = expires;
    options.domain = this.domain;
    options.secure = false;
    options.path = '/';

    this._cookieService.set(COOKIE_PREFIX + this.cookieSuffix, lang, options);
  }
}
